import { createLocation } from './fork/history/LocationUtils';
import { createPath } from './fork/history/PathUtils';
import createTransitionManager, {
  type Prompt,
} from './fork/history/createTransitionManager';
import type { History, Location, Action } from './fork/history';
import type { BrowserHistoryAPI } from './initialize';

/**
 * A custom history implementation for nested apps (granite remotes and the local app rendered by GraniteLocalApp).
 *
 * Offers the same surface area as createBrowserHistory but with one key
 * difference: it never registers its own window.popstate listener. The host
 * (usePopstateCapture) owns all Back/Forward event handling and calls
 * history.replace() to keep this history's location in sync.
 *
 * All browser side-effects (pushState, replaceState, go) are gated on a
 * BrowserHistoryAPI reference that is set via _connect() and cleared by the
 * returned cleanup. Calling cleanup() fully severs the tie to the browser,
 * so an unmounting remote app cannot manipulate browser history.
 */
export type NestedHistory = History & {
  readonly _connect: (browser: BrowserHistoryAPI) => () => void;
};

export function createNestedHistory(
  basename: string,
  initialPath: string,
  initialSearch = ''
): NestedHistory {
  let browser: BrowserHistoryAPI | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const transitionManager = createTransitionManager<any>();

  function createKey(): string {
    return Math.random().toString(36).substring(2, 8);
  }

  let currentLocation: Location = createLocation(
    initialPath + initialSearch,
    undefined,
    createKey()
  );
  let currentAction: Action = 'POP';

  function setState(action: Action, location: Location): void {
    currentAction = action;
    currentLocation = location;
    transitionManager.notifyListeners(location, action);
  }

  const history: NestedHistory = {
    get length() {
      return browser ? browser.length : 1;
    },

    get action() {
      return currentAction;
    },

    get location() {
      return currentLocation;
    },

    push(path, state?) {
      const location = createLocation(
        path,
        state,
        createKey(),
        currentLocation
      );
      transitionManager.confirmTransitionTo(
        location,
        'PUSH',
        undefined,
        (ok) => {
          if (!ok) return;
          browser?.pushState(
            { key: location.key },
            '',
            basename + createPath(location)
          );
          setState('PUSH', location);
        }
      );
    },

    replace(path, state?) {
      const location = createLocation(
        path,
        state,
        createKey(),
        currentLocation
      );
      transitionManager.confirmTransitionTo(
        location,
        'REPLACE',
        undefined,
        (ok) => {
          if (!ok) return;
          browser?.replaceState(
            browser?.state ?? null,
            '',
            basename + createPath(location)
          );
          setState('REPLACE', location);
        }
      );
    },

    go(n) {
      // Delegated to the browser when connected; no-op when severed or n===0.
      // go(0) would reload the page in some browsers — skip it.
      // The location sync happens asynchronously — the host catches the
      // resulting popstate and calls history.replace() to update location.
      if (n !== 0) {
        browser?.go(n);
      }
    },

    goBack() {
      history.go(-1);
    },

    goForward() {
      history.go(1);
    },

    listen(listener) {
      return transitionManager.appendListener(listener);
    },

    block(prompt: Prompt = false) {
      // Tracks the prompt for push/replace confirmation within the remote.
      // Does NOT register a DOM popstate listener — the host owns Back/Forward.
      return transitionManager.setPrompt(prompt);
    },

    createHref(location) {
      return basename + createPath(location);
    },

    _connect(b: BrowserHistoryAPI): () => void {
      browser = b;
      return () => {
        browser = null;
      };
    },
  };

  return history;
}
